import { useCallback, useEffect, useMemo, useState } from 'react';
import Toast from 'react-native-root-toast';

import { POS_TYPE } from '../../../common/constants';
import { MANUAL_RESTOCK } from '../../../common/graphql/serviceRun';
import { useUserContext } from '../../../common/utils/context';
import { useForm } from '../../../common/utils/hooks';
import { getFullMachineName } from '../../../common/utils/machine';
import getPOSModelName from '../../../common/utils/machine/getPOSModelName';
import { getLocalTime } from '../../../common/utils/time/time';
import { MANUAL_RESTOCK_WARNING } from '../../../common/utils/vending-settings';
import { Alert } from '../../components/other/Alert';
import { useOfflineMutation } from '../../offline';

const MARKET_RESTOCK_WARNING = [
	'Before restocking this micro market, make sure the pick list is up to date and includes all products, including fresh food quantities.',
	'If needed, go back and update the pick list before continuing.',
].join('\n\n');

const useManualRestockFlow = ({
	serviceDocketId,
	isRestocked,
	startedProcessing,
	machine,
	planned,
	onManualRestockSuccess,
	disableOffline = false,
	adminTraining = false,
}) => {
	const { tenantSettings, tenant } = useUserContext();
	const [isManualRestockWarningOpen, setIsManualRestockWarningOpen] = useState(false);
	const initialValues = useMemo(() => ({}), []);
	const { values, changeFormValue, resetForm } = useForm(initialValues);

	const isMarket = machine?.type === POS_TYPE.MARKET;
	const posModelName = useMemo(() => (machine ? getPOSModelName(machine) : 'machine'), [machine]);
	const useManualRestockDetails = Boolean(tenantSettings?.useManualRestockDetails);

	const [manualRestockMutation] = useOfflineMutation(MANUAL_RESTOCK, {
		configKey: 'manualRestock',
		disableOffline,
	});
	const manualRestock = useCallback(async () => {
		if (!serviceDocketId) {
			return;
		}

		try {
			const result = await manualRestockMutation({
				variables: {
					serviceDocketId,
					manualRestockComment: values.manualRestockComment,
					manualRestockApprovedBy: values.manualRestockApprovedBy?.id,
					queuedOffline: false,
					...(adminTraining ? { adminTraining: true } : {}),
				},
				context: {
					offlineMeta: {
						machineDetails: machine,
						machineName: machine ? getFullMachineName(machine, machine.place, machine.location) : null,
						machineId: machine?.id,
					},
				},
			});

			// Queued offline: the optimistic update already advanced the docket,
			// and a refetch would fail without connection.
			if (result?.offline) {
				Toast.show('Restock queued; will sync when back online');
				return;
			}

			if (onManualRestockSuccess) {
				await onManualRestockSuccess();
			}
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to restock right now');
		}
	}, [adminTraining, serviceDocketId, manualRestockMutation, values.manualRestockComment, values.manualRestockApprovedBy?.id, onManualRestockSuccess, machine]);

	const isPlannedForToday = useMemo(() => {
		if (!planned) {
			return false;
		}

		return getLocalTime(tenant?.timezone, planned).isSame(getLocalTime(tenant?.timezone), 'day');
	}, [planned, tenant?.timezone]);

	// Keep manual restock form state scoped to the currently selected docket.
	useEffect(() => {
		resetForm();
		setIsManualRestockWarningOpen(false);
	}, [resetForm, serviceDocketId]);

	const openManualRestockAlert = useCallback(() => {
		if (!serviceDocketId) {
			return;
		}

		const shouldOpenManualRestockAlert = !isRestocked &&
			!startedProcessing &&
			isPlannedForToday;
		const restockTitle = isMarket ? 'Restock' : 'Manual restock';

		if (!isMarket && useManualRestockDetails && shouldOpenManualRestockAlert) {
			setIsManualRestockWarningOpen(true);
		}
		else if (shouldOpenManualRestockAlert) {
			Alert.alert(
				restockTitle,
				isMarket ? MARKET_RESTOCK_WARNING : MANUAL_RESTOCK_WARNING,
				[
					{ text: 'Cancel', onPress: () => console.log('Cancel pressed'), style: 'cancel' },
					{
						text: restockTitle,
						onPress: manualRestock,
					},
				],
				{ cancelable: false },
			);
		}
		else if (!isRestocked && startedProcessing) {
			Alert.alert(
				restockTitle,
				`This ${posModelName.toLowerCase()} is processing`,
				[
					{ text: 'Ok', onPress: () => console.log('OK pressed') },
				],
				{ cancelable: false },
			);
		}
		else if (isRestocked) {
			Alert.alert(
				restockTitle,
				`This ${posModelName.toLowerCase()} has already been restocked`,
				[
					{ text: 'Ok', onPress: () => console.log('OK pressed') },
				],
				{ cancelable: false },
			);
		}
		else if (!isPlannedForToday) {
			Alert.alert(
				restockTitle,
				`You may not perform a ${restockTitle.toLowerCase()} on this machine because it's planned service date is not today`,
				[
					{ text: 'Ok', onPress: () => console.log('OK pressed') },
				],
				{ cancelable: false },
			);
		}
	}, [
		isMarket,
		isPlannedForToday,
		isRestocked,
		manualRestock,
		posModelName,
		serviceDocketId,
		startedProcessing,
		useManualRestockDetails,
	]);

	return {
		openManualRestockAlert,
		manualRestock,
		values,
		changeFormValue,
		isManualRestockWarningOpen,
		setIsManualRestockWarningOpen,
	};
};

export default useManualRestockFlow;
